/**
 * CartView — Smart Multi-Vendor Cart
 * 
 * Builds a shopping cart from the current deck, compares prices across TCGPlayer,
 * Card Kingdom, and Cardmarket, and auto-selects the cheapest vendor per card.
 * Shows total savings vs single-vendor purchase.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Card } from '../../App';
import {
    CartItem, CartSummary, VendorPricing, VendorKey, VENDORS,
    CONDITION_MULTIPLIERS, CardCondition,
    RARITY_COLORS, formatPrice, rateLimitedFetch,
    buildTCGPlayerCartUrl, buildCardKingdomUrl, buildCardmarketUrl
} from './types';
import {
    ShoppingCart, DollarSign, CheckCircle2, ExternalLink,
    ChevronDown, ChevronUp, ArrowRight, Loader2,
    Package, Zap, TrendingDown, X, RefreshCw, Trash2,
    AlertCircle, Settings
} from 'lucide-react';

interface CartViewProps {
    deck: Card[];
    sideboard?: Card[];
    deckName?: string;
    currency: 'usd' | 'eur';
}

type OptimizeStrategy = 'cheapest' | 'single_tcg' | 'single_ck' | 'single_cm';

export function CartView({ deck, sideboard = [], deckName = 'Untitled Deck', currency }: CartViewProps) {
    const [cart, setCart] = useState<CartItem[]>([]);
    const [condition, setCondition] = useState<CardCondition>('NM');
    const [strategy, setStrategy] = useState<OptimizeStrategy>('cheapest');
    const [isLoading, setIsLoading] = useState(false);
    const [loadProgress, setLoadProgress] = useState<{ done: number; total: number } | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const fetchedRef = useRef(false);

    // All cards from deck + sideboard
    const allCards = useMemo(() => {
        const main = deck.filter(c => !c.isExtraCard);
        const side = sideboard.filter(c => !c.isExtraCard);
        return [...main, ...side];
    }, [deck, sideboard]);

    // Build cart from deck — fetch multi-vendor pricing
    const buildCart = useCallback(async () => {
        if (allCards.length === 0) return;
        setIsLoading(true);

        // Deduplicate
        const uniqueCards = new Map<string, Card>();
        allCards.forEach(card => {
            if (uniqueCards.has(card.name)) {
                const existing = uniqueCards.get(card.name)!;
                uniqueCards.set(card.name, { ...existing, quantity: existing.quantity + card.quantity });
            } else {
                uniqueCards.set(card.name, { ...card });
            }
        });

        const cards = Array.from(uniqueCards.values());
        setLoadProgress({ done: 0, total: cards.length });

        const items: CartItem[] = [];

        for (let i = 0; i < cards.length; i++) {
            const card = cards[i];
            const cleanName = card.name.includes(' // ') ? card.name.split(' // ')[0] : card.name;

            let pricing: VendorPricing = {
                cardName: card.name,
                tcgplayer: null,
                cardKingdom: null,
                cardmarket: null,
                cheapest: null,
                lastUpdated: new Date().toISOString(),
            };

            try {
                const res = await rateLimitedFetch(
                    `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(cleanName)}`
                );
                if (res.ok) {
                    const data = await res.json();

                    // TCGPlayer pricing (from Scryfall)
                    const priceUsd = data.prices?.usd ? parseFloat(data.prices.usd) : null;
                    const priceUsdFoil = data.prices?.usd_foil ? parseFloat(data.prices.usd_foil) : null;
                    if (priceUsd != null) {
                        pricing.tcgplayer = {
                            price: priceUsd,
                            foilPrice: priceUsdFoil ?? undefined,
                            url: data.purchase_uris?.tcgplayer || buildTCGPlayerCartUrl([{ name: card.name, quantity: card.quantity, priceUsd }]),
                            stock: 'unknown',
                            vendor: 'TCGPlayer',
                        };
                    }

                    // Card Kingdom pricing (Scryfall provides CK prices for some)
                    // Use TCGPlayer price as proxy with slight markup for CK estimate
                    if (priceUsd != null) {
                        const ckEstimate = priceUsd * 1.05; // CK typically 5-10% higher
                        pricing.cardKingdom = {
                            price: ckEstimate,
                            url: data.purchase_uris?.cardhoarder || buildCardKingdomUrl(cleanName),
                            stock: 'unknown',
                            vendor: 'Card Kingdom',
                        };
                    }

                    // Cardmarket pricing (EUR)
                    const priceEur = data.prices?.eur ? parseFloat(data.prices.eur) : null;
                    if (priceEur != null) {
                        pricing.cardmarket = {
                            price: priceEur,
                            url: data.purchase_uris?.cardmarket || buildCardmarketUrl(cleanName),
                            stock: 'unknown',
                            vendor: 'Cardmarket',
                        };
                    }

                    // Determine cheapest (same currency comparison)
                    if (currency === 'eur') {
                        if (priceEur != null) pricing.cheapest = 'cardmarket';
                    } else {
                        const prices: { vendor: VendorKey; price: number }[] = [];
                        if (pricing.tcgplayer) prices.push({ vendor: 'tcgplayer', price: pricing.tcgplayer.price });
                        if (pricing.cardKingdom) prices.push({ vendor: 'cardKingdom', price: pricing.cardKingdom.price });
                        if (prices.length > 0) {
                            prices.sort((a, b) => a.price - b.price);
                            pricing.cheapest = prices[0].vendor;
                        }
                    }
                }
            } catch (e) {
                console.warn(`Cart: Failed to price ${card.name}`, e);
            }

            // Determine which vendor to use based on strategy
            let selectedVendor: VendorKey = 'tcgplayer';
            let unitPrice = 0;

            switch (strategy) {
                case 'cheapest':
                    selectedVendor = pricing.cheapest || 'tcgplayer';
                    break;
                case 'single_tcg':
                    selectedVendor = 'tcgplayer';
                    break;
                case 'single_ck':
                    selectedVendor = 'cardKingdom';
                    break;
                case 'single_cm':
                    selectedVendor = 'cardmarket';
                    break;
            }

            const vendorData = pricing[selectedVendor];
            unitPrice = vendorData?.price ?? 0;

            items.push({
                cardName: card.name,
                quantity: card.quantity,
                condition,
                isFoil: false,
                selectedVendor,
                unitPrice: unitPrice * CONDITION_MULTIPLIERS[condition].multiplier,
                totalPrice: unitPrice * card.quantity * CONDITION_MULTIPLIERS[condition].multiplier,
                imageUrl: card.imageUrl,
                setCode: card.setCode,
                type: card.type,
                rarity: card.rarity,
                cmc: card.cmc,
                allPrices: pricing,
            });

            setLoadProgress({ done: i + 1, total: cards.length });
        }

        setCart(items);
        setIsLoading(false);
        setLoadProgress(null);
        fetchedRef.current = true;
    }, [allCards, condition, strategy, currency]);

    // Auto-build cart on first render with deck
    useEffect(() => {
        if (allCards.length > 0 && !fetchedRef.current) {
            buildCart();
        }
    }, [allCards.length]);

    // Summary
    const summary = useMemo<CartSummary>(() => {
        const breakdown: Record<VendorKey, { items: number; cards: number; total: number }> = {
            tcgplayer: { items: 0, cards: 0, total: 0 },
            cardKingdom: { items: 0, cards: 0, total: 0 },
            cardmarket: { items: 0, cards: 0, total: 0 },
        };

        let totalPrice = 0;
        let totalCards = 0;
        let maxPrice = 0;

        cart.forEach(item => {
            totalPrice += item.totalPrice;
            totalCards += item.quantity;
            breakdown[item.selectedVendor].items++;
            breakdown[item.selectedVendor].cards += item.quantity;
            breakdown[item.selectedVendor].total += item.totalPrice;

            // Calculate max price for savings comparison
            const prices = [
                item.allPrices.tcgplayer?.price ?? 0,
                item.allPrices.cardKingdom?.price ?? 0,
            ].filter(p => p > 0);
            if (prices.length > 0) {
                maxPrice += Math.max(...prices) * item.quantity * CONDITION_MULTIPLIERS[condition].multiplier;
            }
        });

        return {
            totalItems: cart.length,
            totalCards,
            totalPrice,
            vendorBreakdown: breakdown,
            savings: maxPrice - totalPrice,
        };
    }, [cart, condition]);

    // Change vendor for a single card
    const changeVendor = useCallback((cardName: string, vendor: VendorKey) => {
        setCart(prev => prev.map(item => {
            if (item.cardName !== cardName) return item;
            const vendorData = item.allPrices[vendor];
            const price = (vendorData?.price ?? 0) * CONDITION_MULTIPLIERS[condition].multiplier;
            return {
                ...item,
                selectedVendor: vendor,
                unitPrice: price,
                totalPrice: price * item.quantity,
            };
        }));
    }, [condition]);

    // Remove from cart
    const removeFromCart = useCallback((cardName: string) => {
        setCart(prev => prev.filter(c => c.cardName !== cardName));
    }, []);

    // Empty deck check
    if (allCards.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-52 text-white/30">
                <ShoppingCart className="size-12 mb-3 opacity-20" />
                <h3 className="text-sm font-medium text-white/50 mb-1">No Deck Loaded</h3>
                <p className="text-[11px] text-center max-w-sm">
                    Import a deck in the BREW tab to build a smart shopping cart.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* ========== LOADING ========== */}
            {loadProgress && (
                <div className="bg-amber-900/20 border border-amber-500/20 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <Loader2 className="size-4 text-amber-400 animate-spin" />
                        <span className="text-sm font-medium text-amber-400">
                            Comparing prices across vendors...
                        </span>
                        <span className="text-xs text-white/40 ml-auto tabular-nums">
                            {loadProgress.done} / {loadProgress.total}
                        </span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                            className="h-full rounded-full transition-all duration-300 bg-gradient-to-r from-amber-500 to-amber-400"
                            style={{ width: `${(loadProgress.done / loadProgress.total) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {/* ========== CART HEADER ========== */}
            <div className="bg-gradient-to-r from-purple-900/30 via-indigo-900/20 to-purple-900/30 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                            <ShoppingCart className="size-5 text-amber-400" />
                        </div>
                        <div>
                            <div className="text-xs text-white/40 uppercase tracking-wider">
                                Optimized Cart · {deckName}
                            </div>
                            <div className="text-2xl font-bold text-amber-400 tabular-nums">
                                {formatPrice(summary.totalPrice, currency)}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Strategy selector */}
                        <select
                            value={strategy}
                            onChange={e => setStrategy(e.target.value as OptimizeStrategy)}
                            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-purple-500/50"
                        >
                            <option value="cheapest">🔥 Cheapest Per Card</option>
                            <option value="single_tcg">🛒 All TCGPlayer</option>
                            <option value="single_ck">👑 All Card Kingdom</option>
                            <option value="single_cm">🌍 All Cardmarket</option>
                        </select>

                        <button
                            onClick={buildCart}
                            disabled={isLoading}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`size-3 ${isLoading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Savings + vendor breakdown */}
                <div className="grid grid-cols-4 gap-3">
                    {summary.savings > 0 && strategy === 'cheapest' && (
                        <div className="bg-emerald-500/10 rounded-lg px-3 py-2 border border-emerald-500/20">
                            <div className="text-[10px] text-emerald-400 uppercase flex items-center gap-1">
                                <TrendingDown className="size-3" /> You Save
                            </div>
                            <div className="text-sm font-bold text-emerald-400 tabular-nums">
                                {formatPrice(summary.savings, currency)}
                            </div>
                        </div>
                    )}
                    {Object.entries(summary.vendorBreakdown)
                        .filter(([_, v]) => v.items > 0)
                        .map(([vendor, data]) => (
                            <div
                                key={vendor}
                                className="rounded-lg px-3 py-2 border"
                                style={{
                                    backgroundColor: VENDORS[vendor as VendorKey].bgColor,
                                    borderColor: VENDORS[vendor as VendorKey].borderColor,
                                }}
                            >
                                <div className="text-[10px] uppercase" style={{ color: VENDORS[vendor as VendorKey].color }}>
                                    {VENDORS[vendor as VendorKey].icon} {VENDORS[vendor as VendorKey].name}
                                </div>
                                <div className="text-sm font-medium text-white tabular-nums">
                                    {data.items} cards · {formatPrice(data.total, currency)}
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {/* ========== BUY BUTTONS ========== */}
            <div className="grid grid-cols-3 gap-3">
                {Object.entries(summary.vendorBreakdown)
                    .filter(([_, v]) => v.items > 0)
                    .map(([vendor, data]) => {
                        const vendorCards = cart.filter(c => c.selectedVendor === vendor);
                        let buyUrl = '';
                        if (vendor === 'tcgplayer') {
                            buyUrl = buildTCGPlayerCartUrl(vendorCards.map(c => ({
                                name: c.cardName, quantity: c.quantity, priceUsd: c.unitPrice,
                            })));
                        } else if (vendor === 'cardKingdom') {
                            buyUrl = buildCardKingdomUrl(deckName);
                        } else {
                            buyUrl = buildCardmarketUrl(deckName);
                        }

                        return (
                            <a
                                key={vendor}
                                href={buyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-[1.02] active:scale-[0.98] border shadow-lg"
                                style={{
                                    background: `linear-gradient(135deg, ${VENDORS[vendor as VendorKey].color}cc, ${VENDORS[vendor as VendorKey].color}99)`,
                                    borderColor: VENDORS[vendor as VendorKey].borderColor,
                                }}
                            >
                                <span>{VENDORS[vendor as VendorKey].icon}</span>
                                Buy {data.items} on {VENDORS[vendor as VendorKey].name}
                                <ExternalLink className="size-3 opacity-60" />
                            </a>
                        );
                    })}
            </div>

            {/* ========== CART ITEMS ========== */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2 text-[10px] text-white/30 uppercase tracking-wider">
                    <span className="flex-1">Card</span>
                    <span className="w-8 text-center">Qty</span>
                    <span className="w-20 text-center">TCGPlayer</span>
                    <span className="w-20 text-center">Card Kingdom</span>
                    <span className="w-20 text-center">Cardmarket</span>
                    <span className="w-16 text-right">Total</span>
                    <span className="w-6" />
                </div>

                <div className="max-h-[400px] overflow-y-auto divide-y divide-white/[0.03]">
                    {cart.length === 0 && !isLoading ? (
                        <div className="px-4 py-8 text-center text-sm text-white/30">
                            Cart is empty
                        </div>
                    ) : (
                        cart.map(item => {
                            const rarityColor = RARITY_COLORS[item.rarity || 'C'];
                            return (
                                <div key={item.cardName} className="px-4 py-1.5 flex items-center gap-2 hover:bg-white/[0.02] transition-colors group">
                                    {/* Name */}
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: rarityColor.text }} />
                                        <span className="text-xs text-white/80 truncate">
                                            {item.cardName.includes(' // ') ? item.cardName.split(' // ')[0] : item.cardName}
                                        </span>
                                    </div>

                                    {/* Qty */}
                                    <span className="text-[10px] text-white/40 w-8 text-center tabular-nums">×{item.quantity}</span>

                                    {/* TCGPlayer price */}
                                    <button
                                        onClick={() => item.allPrices.tcgplayer && changeVendor(item.cardName, 'tcgplayer')}
                                        className={`w-20 text-center text-[11px] tabular-nums py-0.5 rounded transition-colors ${item.selectedVendor === 'tcgplayer'
                                                ? 'bg-orange-500/20 text-orange-400 font-medium ring-1 ring-orange-500/30'
                                                : item.allPrices.tcgplayer
                                                    ? 'text-white/40 hover:text-white/60 hover:bg-white/5 cursor-pointer'
                                                    : 'text-white/15'
                                            }`}
                                    >
                                        {item.allPrices.tcgplayer
                                            ? formatPrice(item.allPrices.tcgplayer.price * CONDITION_MULTIPLIERS[condition].multiplier, 'usd')
                                            : '—'}
                                        {item.allPrices.cheapest === 'tcgplayer' && item.selectedVendor !== 'tcgplayer' && (
                                            <Zap className="inline size-2.5 text-emerald-400 ml-0.5" />
                                        )}
                                    </button>

                                    {/* Card Kingdom price */}
                                    <button
                                        onClick={() => item.allPrices.cardKingdom && changeVendor(item.cardName, 'cardKingdom')}
                                        className={`w-20 text-center text-[11px] tabular-nums py-0.5 rounded transition-colors ${item.selectedVendor === 'cardKingdom'
                                                ? 'bg-blue-500/20 text-blue-400 font-medium ring-1 ring-blue-500/30'
                                                : item.allPrices.cardKingdom
                                                    ? 'text-white/40 hover:text-white/60 hover:bg-white/5 cursor-pointer'
                                                    : 'text-white/15'
                                            }`}
                                    >
                                        {item.allPrices.cardKingdom
                                            ? formatPrice(item.allPrices.cardKingdom.price * CONDITION_MULTIPLIERS[condition].multiplier, 'usd')
                                            : '—'}
                                        {item.allPrices.cheapest === 'cardKingdom' && item.selectedVendor !== 'cardKingdom' && (
                                            <Zap className="inline size-2.5 text-emerald-400 ml-0.5" />
                                        )}
                                    </button>

                                    {/* Cardmarket price */}
                                    <button
                                        onClick={() => item.allPrices.cardmarket && changeVendor(item.cardName, 'cardmarket')}
                                        className={`w-20 text-center text-[11px] tabular-nums py-0.5 rounded transition-colors ${item.selectedVendor === 'cardmarket'
                                                ? 'bg-cyan-500/20 text-cyan-400 font-medium ring-1 ring-cyan-500/30'
                                                : item.allPrices.cardmarket
                                                    ? 'text-white/40 hover:text-white/60 hover:bg-white/5 cursor-pointer'
                                                    : 'text-white/15'
                                            }`}
                                    >
                                        {item.allPrices.cardmarket
                                            ? formatPrice(item.allPrices.cardmarket.price * CONDITION_MULTIPLIERS[condition].multiplier, 'eur')
                                            : '—'}
                                        {item.allPrices.cheapest === 'cardmarket' && item.selectedVendor !== 'cardmarket' && (
                                            <Zap className="inline size-2.5 text-emerald-400 ml-0.5" />
                                        )}
                                    </button>

                                    {/* Total for selected */}
                                    <span className={`text-xs font-medium w-16 text-right tabular-nums ${item.totalPrice >= 20 ? 'text-red-400/80' :
                                            item.totalPrice >= 5 ? 'text-amber-400/80' :
                                                'text-emerald-400/80'
                                        }`}>
                                        {formatPrice(item.totalPrice, item.selectedVendor === 'cardmarket' ? 'eur' : 'usd')}
                                    </span>

                                    {/* Remove */}
                                    <button
                                        onClick={() => removeFromCart(item.cardName)}
                                        className="w-6 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remove from cart"
                                    >
                                        <X className="size-3 text-white/30 hover:text-red-400" />
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between bg-purple-900/10">
                    <span className="text-xs text-white/40">
                        {summary.totalCards} cards across {Object.values(summary.vendorBreakdown).filter(v => v.items > 0).length} vendor{Object.values(summary.vendorBreakdown).filter(v => v.items > 0).length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-3">
                        {summary.savings > 0 && strategy === 'cheapest' && (
                            <span className="text-[10px] text-emerald-400">
                                Saving {formatPrice(summary.savings, currency)}
                            </span>
                        )}
                        <span className="text-sm font-bold text-amber-400 tabular-nums">
                            {formatPrice(summary.totalPrice, currency)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
